import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import "dayjs/locale/es";
import {
  createReservationApi,
  getBarAvailabilityApi,
  holdReservationApi,
  getAppSettings,
} from "../../api/api";
import { FISERV_CARD_OPTIONS } from "../../constants/fiservPaymentOptions";
import { resolveUrl, COUNTRIES } from "./components/ReservaShared";
import { telefonoConPrefijo } from "../../helpers/telefonoWhatsapp";
import { waitlistPrefill, waitlistTokenFromSearch } from "../listaDeEspera/waitlistModel";
import { isOnlineBookingDisabled } from "./onlineBooking";

dayjs.locale("es");

/**
 * All reservation business logic for a single bar.
 * The shell (BarDetail) loads the bar and hands it in as `{ bar, sucursales, casaCentral }`;
 * on a casa central the `bar` is the chosen sede, so every availability, hold and
 * reservation call carries that sede's id. Remount per bar to reset the selection.
 * Returns a `flow` object consumed by BarDetailDesktop and BarDetailMobile.
 * `restoredStep` tells callers which wizard step to jump to on mount:
 *   'form'  → active hold found, jump to data-entry step
 *   'slots' → pending slot selection found (before hold), jump to slot selection
 *   null    → fresh start
 */
export function useReservationFlow({ bar, sucursales = [], casaCentral = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const token = useSelector((state) => state.auth?.accessToken);
  const authUser = useSelector((state) => state.auth?.user || {});

  // Step 1: date + guests
  // Si llega desde el aviso de la lista de espera, abre con el día y las personas anotadas.
  const waitlistToken = waitlistTokenFromSearch(location.search);
  const prefill = waitlistToken ? waitlistPrefill(location.state) : null;
  const [selectedDate, setSelectedDate] = useState(() => {
    const wanted = prefill?.date ? dayjs(prefill.date, "YYYY-MM-DD", true) : null;
    return wanted && wanted.isValid() && !wanted.isBefore(dayjs().startOf("day")) ? wanted : dayjs().startOf("day");
  });
  const [partySize, setPartySize] = useState(() => (Number(prefill?.criteria?.partySize) > 0 ? Number(prefill.criteria.partySize) : 2));
  const [isEditingContext, setIsEditingContext] = useState(false);

  // Step 2: availability + zone+slot selection
  const [availability, setAvailability] = useState({ date: "", slots: [], zones: [] });
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [availError, setAvailError] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);

  // Step 3: hold + form
  const [holdId, setHoldId] = useState(null);
  const [holdExpiresAt, setHoldExpiresAt] = useState(null);
  const [holdZone, setHoldZone] = useState(null);
  const [holdSlotStart, setHoldSlotStart] = useState(null);
  const [holdDateLabel, setHoldDateLabel] = useState(null);
  const [holdLoading, setHoldLoading] = useState(false);
  const [holdError, setHoldError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [consent, setConsent] = useState(true);
  // Opt-in to news and promotions: unchecked by default, never required to book.
  const [marketingOptIn, setMarketingOptIn] = useState(false);

  // Fiserv Payment Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [modalCard, setModalCard] = useState("");
  const [cardOptions, setCardOptions] = useState(FISERV_CARD_OPTIONS);
  const [appSettings, setAppSettings] = useState({});
  const [pendingValues, setPendingValues] = useState(null);

  // Country selector state
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const countryRef = useRef(null);

  // Hold persistence refs
  const holdStateRef = useRef(null);
  const skipSaveOnUnmountRef = useRef(false);
  const isRestoringRef = useRef(false);

  // restoredStep: what step to open to on mount (set during restore effect)
  const [restoredStep, setRestoredStep] = useState(null);

  useEffect(() => {
    const handleDocClick = (e) => {
      if (countryRef.current && !countryRef.current.contains(e.target)) {
        setCountryDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleDocClick);
    return () => document.removeEventListener("mousedown", handleDocClick);
  }, []);

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      name: authUser?.name || "",
      email: authUser?.email || "",
      phone: "",
      dni: authUser?.dni || "",
      notes: "",
    },
  });

  useEffect(() => {
    setValue("name", authUser?.name || "");
    setValue("email", authUser?.email || "");
    setValue("dni", authUser?.dni || "");
  }, [authUser?.name, authUser?.email, authUser?.dni, setValue]);

  // Date constraints
  const windowDays = Number(bar?.reservationWindowDays || 6);
  const minDate = dayjs().startOf("day");
  const maxDate = minDate.add(windowDays, "day");
  const dateLabel = selectedDate.format("YYYY-MM-DD");

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await getAppSettings();
        if (response?.status === 200 && response?.data?.info) {
          setAppSettings(response.data.info);
          const options = response.data.info.fiserv_card_options || response.data.info.fiservCardOptions;
          if (Array.isArray(options) && options.length) {
            setCardOptions(options);
          }
        }
      } catch (_) {}
    };
    fetchSettings();
  }, []);

  const selectedCardOption = useMemo(() => {
    return cardOptions.find((opt) => opt.id === modalCard) || null;
  }, [cardOptions, modalCard]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const bodyEl = document.body;
    if (!bodyEl) return;
    const className = "payment-modal-open";
    if (showPaymentModal) {
      bodyEl.classList.add(className);
    } else {
      bodyEl.classList.remove(className);
    }
    return () => {
      bodyEl.classList.remove(className);
    };
  }, [showPaymentModal]);

  const bookingDisabled = isOnlineBookingDisabled(bar);

  const loadAvailability = useCallback(async (targetDate) => {
    // The API answers 403 for a bar with online booking off: don't even ask.
    if (!bar?._id || bookingDisabled) return;
    try {
      setLoadingAvail(true);
      setAvailError("");
      setSelectedSlot(null);
      setSelectedZone(null);
      const response = await getBarAvailabilityApi(bar._id, targetDate.format("YYYY-MM-DD"));
      const info = response?.data?.info || { date: targetDate.format("YYYY-MM-DD"), slots: [], zones: [] };
      setAvailability(info);
    } catch (err) {
      setAvailability({ date: targetDate.format("YYYY-MM-DD"), slots: [], zones: [] });
      setAvailError(err?.response?.data?.message || "No hay disponibilidad para esa fecha.");
    } finally {
      setLoadingAvail(false);
    }
  }, [bar?._id, bookingDisabled]);

  // Reset slot/zone when date changes
  useEffect(() => {
    if (isRestoringRef.current) {
      isRestoringRef.current = false;
      return;
    }
    setSelectedSlot(null);
    setSelectedZone(null);
  }, [dateLabel]);

  // Keep holdStateRef in sync
  useEffect(() => {
    const holdMatchesCurrent =
      holdId && holdExpiresAt && bar?._id &&
      holdZone === selectedZone &&
      holdSlotStart === selectedSlot?.slotStart &&
      holdDateLabel === dateLabel;
    if (holdMatchesCurrent) {
      holdStateRef.current = { barId: bar._id, dateLabel, partySize, selectedSlot, selectedZone, holdId, holdExpiresAt };
    } else {
      holdStateRef.current = null;
    }
  }, [holdId, holdExpiresAt, holdZone, holdSlotStart, holdDateLabel, bar?._id, dateLabel, partySize, selectedSlot, selectedZone]);

  // Save active hold on SPA navigation (unmount)
  useEffect(() => {
    return () => {
      if (skipSaveOnUnmountRef.current) return;
      const state = holdStateRef.current;
      if (state && new Date(state.holdExpiresAt) > new Date()) {
        try { sessionStorage.setItem("reservation:hold_state", JSON.stringify(state)); } catch (_) {}
      }
    };
  }, []);

  // Save active hold on page refresh / tab close
  useEffect(() => {
    const save = () => {
      if (skipSaveOnUnmountRef.current) return;
      const state = holdStateRef.current;
      if (state && new Date(state.holdExpiresAt) > new Date()) {
        try { sessionStorage.setItem("reservation:hold_state", JSON.stringify(state)); } catch (_) {}
      }
    };
    window.addEventListener("beforeunload", save);
    return () => window.removeEventListener("beforeunload", save);
  }, []);

  // Restore hold state after navigation (login redirect or back navigation)
  useEffect(() => {
    if (!bar?._id) return;
    try {
      // Priority 1: active hold → restore to form step
      const raw = sessionStorage.getItem("reservation:hold_state");
      if (raw) {
        const saved = JSON.parse(raw);
        if (!saved.holdExpiresAt || new Date(saved.holdExpiresAt) <= new Date()) {
          sessionStorage.removeItem("reservation:hold_state");
        } else if (String(saved.barId) === String(bar._id)) {
          sessionStorage.removeItem("reservation:hold_state");
          isRestoringRef.current = true;
          if (saved.dateLabel) setSelectedDate(dayjs(saved.dateLabel, "YYYY-MM-DD"));
          if (saved.partySize) setPartySize(saved.partySize);
          if (saved.selectedSlot) setSelectedSlot(saved.selectedSlot);
          if (saved.selectedZone) setSelectedZone(saved.selectedZone);
          if (saved.holdId) setHoldId(saved.holdId);
          if (saved.holdExpiresAt) setHoldExpiresAt(saved.holdExpiresAt);
          setHoldZone(saved.selectedZone || null);
          setHoldSlotStart(saved.selectedSlot?.slotStart || null);
          setHoldDateLabel(saved.dateLabel || null);
          setRestoredStep("form");
          return;
        }
      }

      // Priority 2: pending slot/zone selection saved before login redirect
      const rawSel = sessionStorage.getItem("reservation:pending_selection");
      if (rawSel) {
        const savedSel = JSON.parse(rawSel);
        if (String(savedSel.barId) === String(bar._id)) {
          sessionStorage.removeItem("reservation:pending_selection");
          isRestoringRef.current = true;
          const restoredDate = savedSel.dateLabel ? dayjs(savedSel.dateLabel, "YYYY-MM-DD") : null;
          if (restoredDate) setSelectedDate(restoredDate);
          if (savedSel.partySize) setPartySize(savedSel.partySize);
          if (savedSel.selectedSlot) setSelectedSlot(savedSel.selectedSlot);
          if (savedSel.selectedZone) setSelectedZone(savedSel.selectedZone);

          if (token) {
            (async () => {
              try {
                setHoldLoading(true);
                setHoldError("");
                const response = await holdReservationApi(
                  {
                    barId: bar._id,
                    zone: savedSel.selectedZone,
                    date: savedSel.dateLabel,
                    slotStart: savedSel.selectedSlot.slotStart,
                    partySize: Number(savedSel.partySize),
                  },
                  token
                );
                const info = response?.data?.info || {};
                setHoldId(info.holdId);
                setHoldExpiresAt(info.expiresAt);
                setHoldZone(savedSel.selectedZone);
                setHoldSlotStart(savedSel.selectedSlot.slotStart);
                setHoldDateLabel(savedSel.dateLabel);
                setRestoredStep("form");
              } catch (err) {
                setHoldError(err?.response?.data?.message || "No pudimos bloquear el lugar. Intentá de nuevo.");
                setRestoredStep("slots");
                if (restoredDate) loadAvailability(restoredDate);
              } finally {
                setHoldLoading(false);
              }
            })();
          } else {
            setRestoredStep("slots");
            if (restoredDate) loadAvailability(restoredDate);
          }
        }
      }
    } catch (_) {}
  }, [bar?._id, loadAvailability, token]);

  // Price display helpers
  const getZonePrice = (zone) => {
    if (!zone) return null;
    const zoneDef = availability.zones?.find((z) => z.name === zone);
    if (!zoneDef) return null;
    if (bar?.reservationMode !== "deposit") return null;
    if (bar?.zonePricingEnabled && zoneDef.priceEnabled && zoneDef.price > 0) {
      return `$${Number(zoneDef.price).toLocaleString("es-AR")}`;
    }
    if (bar?.depositAmount > 0) return `$${Number(bar.depositAmount).toLocaleString("es-AR")}`;
    return "Sin seña";
  };

  const displayPrice = selectedZone
    ? getZonePrice(selectedZone)
    : bar?.depositAmount > 0
      ? `$${Number(bar.depositAmount).toLocaleString("es-AR")}`
      : "Sin seña";

  // Zones available for the selected slot
  const availableZonesForSlot = selectedSlot
    ? (availability.zones || []).filter((z) => {
        const slotData = z.slots?.find((s) => s.slotStart === selectedSlot.slotStart);
        return slotData?.available;
      })
    : [];

  // Slots that have at least one available zone
  const availableSlotsData = (availability.slots || []).map((slot) => {
    const hasAvailability = (availability.zones || []).some((z) => {
      const sd = z.slots?.find((s) => s.slotStart === slot.slotStart);
      return sd?.available;
    });
    return { ...slot, hasAvailability };
  });

  // Create hold then advance to form (auth gate)
  const handleAdvanceToForm = async (zoneOverride = null) => {
    const zoneToUse = zoneOverride || selectedZone;
    if (!selectedSlot || !zoneToUse) return;

    if (!token) {
      try {
        sessionStorage.setItem("reservation:pending_selection", JSON.stringify({
          barId: bar._id,
          dateLabel,
          partySize,
          selectedSlot,
          selectedZone: zoneToUse,
        }));
      } catch (_) {}
      navigate("/login", { state: { from: location.pathname + location.search } });
      return;
    }

    // Reuse existing hold if valid and same parameters
    const holdStillValid = holdId && holdExpiresAt && new Date(holdExpiresAt) > new Date();
    if (
      holdStillValid &&
      holdZone === zoneToUse &&
      holdSlotStart === selectedSlot.slotStart &&
      holdDateLabel === dateLabel
    ) {
      return { reused: true };
    }

    try {
      setHoldLoading(true);
      setHoldError("");
      const response = await holdReservationApi(
        {
          barId: bar._id,
          zone: zoneToUse,
          date: dateLabel,
          slotStart: selectedSlot.slotStart,
          partySize: Number(partySize),
        },
        token
      );
      const info = response?.data?.info || {};
      setHoldId(info.holdId);
      setHoldExpiresAt(info.expiresAt);
      setHoldZone(zoneToUse);
      setHoldSlotStart(selectedSlot.slotStart);
      setHoldDateLabel(dateLabel);
      return { created: true };
    } catch (err) {
      setHoldError(err?.response?.data?.message || "No pudimos bloquear el lugar. Intentá de nuevo.");
      return { error: true };
    } finally {
      setHoldLoading(false);
    }
  };

  const handleHoldExpired = useCallback(() => {
    setHoldId(null);
    setHoldExpiresAt(null);
    setHoldZone(null);
    setHoldSlotStart(null);
    setHoldDateLabel(null);
    setSelectedSlot(null);
    setSelectedZone(null);
    loadAvailability(selectedDate);
    // Returns 'slots' so callers know to go back to slot selection
    return "slots";
  }, [loadAvailability, selectedDate]);

  const proceedWithReservation = async (values, storeId = null) => {
    try {
      setSubmitting(true);
      setSubmitError("");
      // The country picked in the phone field decides the prefix, like the other booking flows.
      const formattedPhone = telefonoConPrefijo(values.phone, selectedCountry?.prefix);

      const payload = {
        barId: bar._id,
        zone: selectedZone,
        holdId,
        date: dateLabel,
        slotStart: selectedSlot.slotStart,
        partySize: Number(partySize),
        name: values.name,
        email: values.email,
        phone: formattedPhone,
        dni: values.dni,
        notes: values.notes,
        checkoutOrigin: window.location.origin,
      };
      if (waitlistToken) payload.waitlistToken = waitlistToken;
      if (marketingOptIn) payload.marketingOptIn = true;
      if (storeId) {
        payload.storeId = storeId;
        payload.numberOfInstallments = 1;
      }
      const response = await createReservationApi(payload, token);
      const info = response?.data?.info || {};
      const reservationCode = info?.reservationCode;
      sessionStorage.setItem(
        "reservation:last",
        JSON.stringify({
          flow: "reservas",
          reservationCode,
          barSlugOrId: bar?.slug || bar?._id,
          barId: bar?._id,
          date: payload.date,
          slotStart: payload.slotStart,
          zone: selectedZone,
        })
      );
      skipSaveOnUnmountRef.current = true;
      sessionStorage.removeItem("reservation:hold_state");
      if (info?.paymentHref) {
        window.location.href = resolveUrl(info.paymentHref);
        return;
      }
      navigate(`/reservas/success?code=${encodeURIComponent(String(reservationCode || ""))}`);
    } catch (err) {
      if (err?.response?.status === 409) {
        const msg = err?.response?.data?.message || "";
        // "seña": el lugar no puede cobrar señas ahora; no es un bloqueo vencido.
        if (msg.includes("reserva") || msg.includes("seña") || msg.includes("límite") || msg.includes("limite")) {
          setSubmitError(msg);
        } else {
          setSubmitError("El bloqueo expiró o la zona se ocupó. Seleccioná nuevamente.");
          setHoldId(null);
          setHoldExpiresAt(null);
          setHoldZone(null);
          setHoldSlotStart(null);
          setHoldDateLabel(null);
          setSelectedSlot(null);
          setSelectedZone(null);
          await loadAvailability(selectedDate);
          // Return 'slots' so callers can navigate back
          return "slots";
        }
      } else if (err?.response?.status === 401 || err?.response?.status === 402) {
        navigate("/login", { state: { from: location.pathname + location.search } });
      } else {
        setSubmitError(err?.response?.data?.message || "No pudimos crear la reserva.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (values) => {
    if (!token) {
      try {
        sessionStorage.setItem("reservation:hold_state", JSON.stringify({
          barId: bar._id,
          dateLabel,
          partySize,
          selectedSlot,
          selectedZone,
          holdId,
          holdExpiresAt,
        }));
      } catch (_) {}
      navigate("/login", { state: { from: location.pathname + location.search } });
      return;
    }

    if (bar.reservationMode === "deposit") {
      setPendingValues(values);
      const defaultCard = cardOptions?.[0]?.id || "";
      setModalCard(defaultCard);
      setShowPaymentModal(true);
      return;
    }

    return await proceedWithReservation(values);
  };

  const handleConfirmPaymentSelection = () => {
    const selectedCard = cardOptions.find(opt => opt.id === modalCard);
    if (!selectedCard || !selectedCard.storeId) return;
    setShowPaymentModal(false);
    if (pendingValues) {
      proceedWithReservation(pendingValues, selectedCard.storeId);
    }
  };

  return {
    // bar
    bar,
    sucursales,
    casaCentral,
    // lista de espera: token del aviso (si vino de ahí)
    waitlistToken,
    // date / guests
    selectedDate,
    setSelectedDate,
    partySize,
    setPartySize,
    isEditingContext,
    setIsEditingContext,
    minDate,
    maxDate,
    dateLabel,
    // availability
    availability,
    loadingAvail,
    availError,
    loadAvailability,
    availableSlotsData,
    availableZonesForSlot,
    // slot + zone selection
    selectedSlot,
    setSelectedSlot,
    selectedZone,
    setSelectedZone,
    // hold
    holdId,
    holdExpiresAt,
    holdLoading,
    holdError,
    handleAdvanceToForm,
    handleHoldExpired,
    // form
    register,
    handleSubmit,
    setValue,
    trigger,
    errors,
    submitting,
    submitError,
    consent,
    setConsent,
    marketingOptIn,
    setMarketingOptIn,
    onSubmit,
    // country selector
    selectedCountry,
    setSelectedCountry,
    countryDropdownOpen,
    setCountryDropdownOpen,
    countryRef,
    // Fiserv
    showPaymentModal,
    setShowPaymentModal,
    modalCard,
    setModalCard,
    cardOptions,
    selectedCardOption,
    pendingValues,
    setPendingValues,
    handleConfirmPaymentSelection,
    appSettings,
    // price helpers
    getZonePrice,
    displayPrice,
    // restore
    restoredStep,
  };
}
